import { createApp } from 'vue'
import { createPinia } from 'pinia'
import router from './router'
import App from './App.vue'
import { useAuthStore } from './stores/auth.store'
import { createAuthService } from './services/auth-service.factory'

import './styles/main.css'

import * as Sentry from '@sentry/vue'
import { reportError, handleRouterError } from './lib/error-reporting'
import {
  sentryBeforeSend,
  sentryBeforeSendTransaction,
  sentryBeforeBreadcrumb,
} from './lib/sentry-scrub'
import { installSessionReplayGate } from './lib/sentry-replay-gate'
import { installAccountAccessWatch } from './lib/account-access'

// US #1814 (Task #1818 A24b), LIVE: notice the BFF refusing a disabled account or a removed
// membership, on ANY request. Installed first, before auth initialises, so the sign-in's own
// `/user-roles/me` is already observed. See `lib/account-access.ts`.
installAccountAccessWatch()

// Create app instance
const app = createApp(App)
const pinia = createPinia()

// Initialise Sentry as early as possible — reads config from VITE_SENTRY_* env vars.
// VITE_SENTRY_ENABLED=true is injected ONLY by the stable branch GitHub Actions deploy.
// This means Sentry is completely silent in dev, test, and CI — production only.
const sentryEnabled = import.meta.env.VITE_SENTRY_ENABLED === 'true'

if (sentryEnabled && import.meta.env.VITE_SENTRY_DSN) {
  // Owns Session Replay's whole lifecycle — see `lib/sentry-replay-gate.ts`. It must run BEFORE
  // `Sentry.init` (its `popstate` listener has to precede the SDK's own history instrumentation)
  // and before `app.use(router)` (its router hooks have to see the very first navigation).
  installSessionReplayGate(router)

  Sentry.init({
    app,
    dsn: import.meta.env.VITE_SENTRY_DSN as string,
    environment: (import.meta.env.VITE_SENTRY_ENVIRONMENT as string) || 'development',

    // Automatic instrumentation: Vue Router tracing + performance.
    //
    // Session Replay is deliberately absent here. Listing it starts recording during `init`,
    // which is before the router has resolved anything — and a link opened from an email paints
    // straight onto a token-bearing route, so that first recording would already hold a live
    // bearer token in its `urls`, on a surface no `beforeSend` hook can reach. The gate
    // registers it — via `installSessionReplayGate(router)` above, with NO controller argument,
    // so `createSentryReplayController()`'s own defaults apply: `maskAllText: true` and
    // `blockAllMedia: true`. That is MORE masking than this file configured when Replay was
    // still listed here at init time (`false`/`false` on both) — a deliberate tightening, not a
    // regression — so do not "restore" `false`/`false` to match a stale comment; the sample
    // rates below still apply once the gate starts the integration.
    integrations: [Sentry.browserTracingIntegration({ router })],

    // Tracing — production only so always use production sample rates
    tracesSampleRate: 0.2,

    // Propagate trace headers to BFF API and Site Compass API endpoints
    tracePropagationTargets: [/\/api\//, /^https:\/\/.*\.sitecompass\.com\.au\/api/],

    // Session Replay sampling. Still read from here: the gate registers the integration on the
    // live client, which is where the SDK makes its own sampling decision from these.
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,

    // Structured logs (requires enableLogs on Sentry SDK)
    enableLogs: true,

    // Capture default PII (IP address etc.) — review privacy policy before enabling in prod
    sendDefaultPii: false,

    // Redact the invite/registration/proposal token segment from every URL Sentry can see —
    // events, transactions and breadcrumbs are separate capture surfaces, so all three hooks
    // are wired. See `lib/sentry-scrub.ts` for the route shapes and what each hook scrubs.
    beforeSend: sentryBeforeSend,
    beforeSendTransaction: sentryBeforeSendTransaction,
    beforeBreadcrumb: sentryBeforeBreadcrumb,
  })
}

// Global Vue error handler — guarantees render/lifecycle/watcher errors reach Sentry
// (set after Sentry.init so this is the active handler; reportError still captures to Sentry).
app.config.errorHandler = (err, _instance, info) => {
  reportError(err, { where: 'vue:errorHandler', extra: { lifecycleHook: info } })
}

// IMPORTANT: Register Pinia and Router BEFORE mounting
// This ensures stores and router are available when components initialize
app.use(pinia)
app.use(router)

// Navigation progress indicator — registered before authGuard so the indicator
// fires at the very start of every navigation, including during auth checks.
import { useNavigationStore } from './stores/navigation.store'
router.beforeEach(() => {
  useNavigationStore().startNavigation()
})
router.afterEach(() => {
  useNavigationStore().endNavigation()
  // A successful navigation means chunks loaded fine — reset the stale-chunk reload guard
  // so a future deploy can trigger a fresh one-time recovery reload.
  try {
    sessionStorage.removeItem('sc-chunk-reloaded')
  } catch {
    /* sessionStorage unavailable (private mode) — non-fatal */
  }
})
router.onError(error => {
  useNavigationStore().endNavigation()
  handleRouterError(error)
})

// Register auth guard after Pinia is initialized
import { authGuard } from './router/guards/auth.guard'
router.beforeEach(authGuard)

// A response can raise the no-seat flag between navigations: move the member now (#1848)
import { installSeatRedirect } from './router/guards/seat-redirect'
installSeatRedirect(router, useAuthStore())

// Wait for router to be ready before initializing auth
router.isReady().then(() => {
  // Initialize auth service (now async)
  createAuthService()
    .then(authService => {
      const authStore = useAuthStore()
      authStore.setAuthService(authService)

      // Initialize auth state
      return authStore.initialize()
    })
    .then(() => {
      // Mount app only after everything is ready
      app.mount('#app')
    })
    .catch(error => {
      console.error('Auth initialization error:', error)
      app.config.globalProperties.$authInitError = error
      // Still mount the app to show error state
      app.mount('#app')
    })
})
